import { Container, Graphics, Text } from "pixi.js";

import { sailPower, WIND_NO_GO } from "../../../shared/Wind";

/** Panel size, in screen pixels. The width is the minimap's, so the right column reads as one. */
const WIDTH = 168;
const HEIGHT = 76;
/** Gap from the screen edge, and from the map below. Matches every other panel. */
const MARGIN = 16;
/** Height of the minimap this sits above — see src/scripts/ui/Minimap.ts. */
const MAP_SIZE = 168;
const CORNER_RADIUS = 8;

const PANEL_COLOR = 0x071a21;
const PANEL_ALPHA = 0.55;
const BORDER_COLOR = 0xf2e2c4;
const BORDER_ALPHA = 0.35;
const BORDER_WIDTH = 2;

/** Cream, as everywhere else in the HUD. */
const TEXT_COLOR = 0xf2e2c4;
/** You. The action red, the same as the minimap's dot and the leaderboard's row. */
const PLAYER_COLOR = 0xc8452f;
const LABEL_SIZE = 13;
const LABEL_X = 16;

/** The dial: where it sits in the panel, and how big it is. */
const DIAL_X = 120;
const DIAL_Y = HEIGHT / 2;
const DIAL_RADIUS = 25;
/** The compass ring and its four ticks, dim enough to read as a frame rather than as data. */
const RING_ALPHA = 0.22;
const TICK_ALPHA = 0.3;
/** North is the one tick worth telling apart: it is what says the dial is north-up. */
const NORTH_ALPHA = 0.6;
const TICK_LENGTH = 4;

/**
 * The wing of sailable headings: the rim it grows out of, how far it reaches at full power, and
 * how finely its edge is stepped.
 *
 * **Thickness rather than brightness.** The obvious drawing is a ring of segments whose alpha is
 * `sailPower`, and it stripes: sixty translucent strokes abutting each other double up wherever
 * two meet, so the band comes out combed. One solid polygon has no seams to blend, costs one fill,
 * and says the same thing more loudly — the wing is fat where the ship goes fast and pinches to
 * nothing at the edge of the dead sector.
 */
const BAND_INNER = 13;
const BAND_DEPTH = 8;
const BAND_STEPS = 48;
const BAND_ALPHA = 0.42;
/** A crisp line along the wing's edge, so the shape reads without its fill having to shout. */
const BAND_EDGE_ALPHA = 0.8;
/** The rim the wing grows from, drawn all the way round so the dead sector reads as an empty groove. */
const TRACK_ALPHA = 0.16;

/** The needle, drawn pointing north and then rotated. Inside the wing's rim, so the two never meet. */
const NEEDLE_LENGTH = 11;
const NEEDLE_HALF_WIDTH = 4.5;
/** How far the tail reaches back from the centre, and how wide it is drawn. */
const TAIL_LENGTH = 8;
const TAIL_WIDTH = 2;

/** The caret that marks the player's own heading, just outside the ring. */
const CARET_INNER = DIAL_RADIUS + 2;
const CARET_OUTER = DIAL_RADIUS + 8;
const CARET_HALF_WIDTH = 5;
/** Where the wing reaches zero: the closest to the wind a hull can point and still draw. */
const SAIL_LIMIT = Math.PI - WIND_NO_GO;

/**
 * The wind vane: which way the wind is blowing, **which headings that leaves worth sailing**, and
 * where this ship is pointed among them. Bottom-right, directly above the minimap.
 *
 * It began as an arrow and nothing else, which was the whole story while the wind decided nothing.
 * Now that a hull's speed is its heading against the weather (design/Wind.md), the question a
 * captain actually has is not "where is the wind" but **"where can I go"** — so the dial answers
 * that one:
 *
 * - a **wing of sailable headings** around the rim, thickest dead downwind and pinching to nothing
 *   at the edge of the no-go sector. It is `sailPower` itself, drawn round a circle: the gap in it
 *   *is* the dead water, at whatever `windNoGoAngle` the dashboard is set to;
 * - the **arrow**, still the way the wind blows — which is also the fastest heading a hull can sail;
 * - a **red caret** for the ship's own heading, in the colour the minimap and the leaderboard
 *   already use to mean "you". Caret inside the band means you are sailing; caret in the gap means
 *   you are in irons, and the shortest way out is whichever end of the gap is nearer.
 *
 * **Nothing here is a number**, which is the point: the whole mechanic is legible at a glance and
 * in one colour language. See design/UI.md.
 *
 * It sits above the map because the two share a frame of reference — both are **north-up and
 * absolute** — so a bearing read on one means the same thing on the other without re-orienting.
 * The dial's four ticks are what say so, and north is the bright one.
 *
 * **Nothing is redrawn per frame.** The band and the arrow are one `Graphics` built in the wind's
 * own frame and turned by the wind; the caret is another, turned by the heading. A `Graphics`
 * rebuilt every frame re-tessellates on the GPU every frame, and this panel is the last thing in
 * the game that should cost anything.
 */
export class WindVane {
    private readonly _container = new Container();
    /** Everything that turns with the weather: the sailable band and the needle. */
    private readonly _wind = new Container();
    /** The player's own heading marker, turned by the hull rather than by the wind. */
    private readonly _heading = new Container();

    public get container(): Container {
        return this._container;
    }

    constructor() {
        // The panel, the label and the dial's frame never change, so they are drawn once.
        const panel = new Graphics().roundRect(0, 0, WIDTH, HEIGHT, CORNER_RADIUS);
        panel.fill({ color: PANEL_COLOR, alpha: PANEL_ALPHA });
        panel.stroke({ color: BORDER_COLOR, alpha: BORDER_ALPHA, width: BORDER_WIDTH });

        const label = new Text({
            text: "WIND",
            x: LABEL_X,
            y: HEIGHT / 2,
            anchor: { x: 0, y: 0.5 },
            style: { fontFamily: "Grobold", fontSize: LABEL_SIZE, fill: TEXT_COLOR }
        });

        this._container.addChild(panel, label, this._dial(), this._wind, this._heading);
        this._wind.position.set(DIAL_X, DIAL_Y);
        this._heading.position.set(DIAL_X, DIAL_Y);

        this._wind.addChild(this._band(), this._needle());
        this._heading.addChild(this._caret());
    }

    /**
     * Turns the dial to the arena's wind and the caret to the hull's heading — both in radians,
     * 0 = north, growing clockwise.
     *
     * Set straight rather than eased onto, unlike a remote hull's position: the wind veers at
     * WIND_VEER_RATE, which is a fraction of a degree between two patches, and the heading is the
     * player's own prediction rather than anything that arrives late. Pixi's rotation grows
     * clockwise from north on a shape drawn pointing up, which is the game's heading convention
     * exactly — see `shared/Wind.ts`.
     */
    public update(wind: number, heading: number): void {
        this._wind.rotation = wind;
        this._heading.rotation = heading;
    }

    public resize(width: number, height: number): void {
        this._container.position.set(width - WIDTH - MARGIN, height - MAP_SIZE - MARGIN - MARGIN - HEIGHT);
    }

    public destroy(): void {
        this._container.destroy({ children: true });
    }

    /** The static frame: the ring, and four ticks that say the dial is north-up. */
    private _dial(): Graphics {
        const dial = new Graphics();
        dial.circle(DIAL_X, DIAL_Y, DIAL_RADIUS).stroke({ color: BORDER_COLOR, alpha: RING_ALPHA, width: 1 });

        // Drawn from the ring inwards, so the outer edge of the dial stays a clean circle.
        for (let quarter = 0; quarter < 4; quarter++) {
            const angle = (quarter * Math.PI) / 2;
            const dx = Math.sin(angle);
            const dy = -Math.cos(angle);
            dial.moveTo(DIAL_X + dx * DIAL_RADIUS, DIAL_Y + dy * DIAL_RADIUS);
            dial.lineTo(DIAL_X + dx * (DIAL_RADIUS - TICK_LENGTH), DIAL_Y + dy * (DIAL_RADIUS - TICK_LENGTH));
            dial.stroke({ color: BORDER_COLOR, alpha: quarter === 0 ? NORTH_ALPHA : TICK_ALPHA, width: quarter === 0 ? 2 : 1 });
        }

        return dial;
    }

    /**
     * The sailable wing, drawn **in the wind's own frame** — the point at offset `beta` from dead
     * downwind reaches `sailPower(0, beta)` out from the rim, so the whole shape is built once and
     * simply turned as the weather veers. Rebuilding it per frame would be the same picture,
     * re-tessellated sixty times a second.
     *
     * One polygon, out along the powered edge and back along the rim. It spans only the headings
     * that draw — the curve is zero outside them and the shape would be a sliver — so at
     * `windNoGoAngle` of zero it closes into a full ring, and at 90 it is a half moon.
     */
    private _band(): Graphics {
        const band = new Graphics();
        band.circle(0, 0, BAND_INNER).stroke({ color: TEXT_COLOR, alpha: TRACK_ALPHA, width: 1.5 });

        const points: number[] = [];
        const at = (i: number) => -SAIL_LIMIT + (2 * SAIL_LIMIT * i) / BAND_STEPS;

        // Out along the edge power describes...
        for (let i = 0; i <= BAND_STEPS; i++) {
            const beta = at(i);
            const radius = BAND_INNER + sailPower(0, beta) * BAND_DEPTH;
            points.push(Math.sin(beta) * radius, -Math.cos(beta) * radius);
        }
        // ...and back along the rim it grew from.
        for (let i = BAND_STEPS; i >= 0; i--) {
            const beta = at(i);
            points.push(Math.sin(beta) * BAND_INNER, -Math.cos(beta) * BAND_INNER);
        }

        // Filled quietly and edged brightly: the fill is a quantity and the edge is its shape, and
        // between them they leave the needle the brightest cream on the dial rather than competing
        // with it.
        band.poly(points, true).fill({ color: TEXT_COLOR, alpha: BAND_ALPHA });
        band.stroke({ color: TEXT_COLOR, alpha: BAND_EDGE_ALPHA, width: 1 });

        return band;
    }

    /** The wind's own arrow, drawn pointing along the container's north and turned with it. */
    private _needle(): Graphics {
        const arrow = new Graphics();
        arrow.poly([0, -NEEDLE_LENGTH, NEEDLE_HALF_WIDTH, 0, -NEEDLE_HALF_WIDTH, 0], true).fill(TEXT_COLOR);
        arrow.rect(-TAIL_WIDTH / 2, 0, TAIL_WIDTH, TAIL_LENGTH).fill(TEXT_COLOR);

        return arrow;
    }

    /**
     * The player's heading marker: a wedge outside the ring, pointing in at the band. Outside
     * rather than in, so it never sits on top of the arrow — the one reading that matters is
     * whether the caret is over a lit part of the band, and two overlapping shapes at the centre
     * would be exactly where that gets hard to see.
     */
    private _caret(): Graphics {
        return new Graphics()
            .poly([0, -CARET_INNER, CARET_HALF_WIDTH, -CARET_OUTER, -CARET_HALF_WIDTH, -CARET_OUTER], true)
            .fill(PLAYER_COLOR);
    }
}
